import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../hooks/useApi';
import { useTeams } from '../hooks/useTeams';
import { Card, Button, Select, Spinner, PageHeader, EmptyState } from '../components/ui';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

interface Visit {
    id: string;
    customer_name: string;
    latlng: string;
    lat?: number;
    lng?: number;
    team: string;
    date: string;
    province: string;
    district: string;
    phone?: string;
    customer_type?: string;
}

type DatePreset = 'today' | '7days' | '30days' | 'month' | 'custom' | 'all';

// Default Team Colors (Fallback)
const TEAM_COLORS: Record<string, string> = {
    'A': '#EF4444', // Red
    'B': '#10B981', // Emerald
    'C': '#8B5CF6', // Violet
    'D': '#F59E0B', // Amber
};

export function MapView() {
    const { teams } = useTeams();
    const [visits, setVisits] = useState<Visit[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTeam, setSelectedTeam] = useState('');
    const [datePreset, setDatePreset] = useState<DatePreset>('all');
    const [dateRange, setDateRange] = useState({
        from: '2020-01-01',
        to: new Date().toISOString().split('T')[0],
    });
    const [hiddenTeams, setHiddenTeams] = useState<Set<string>>(new Set());
    const [mapReady, setMapReady] = useState(false);

    // Location Tracking State
    const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [isTracking, setIsTracking] = useState(false);
    const watchIdRef = useRef<number | null>(null);

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);
    const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const userMarkerRef = useRef<google.maps.Marker | null>(null);

    // Date preset handler
    const applyDatePreset = (preset: DatePreset) => {
        setDatePreset(preset);
        const today = new Date();
        let from: Date;

        switch (preset) {
            case 'today':
                from = today;
                break;
            case '7days':
                from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                break;
            case '30days':
                from = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
                break;
            case 'month':
                from = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            case 'all':
                from = new Date('2020-01-01');
                break;
            default:
                return;
        }

        setDateRange({
            from: from.toISOString().split('T')[0],
            to: today.toISOString().split('T')[0],
        });
    };

    // Load visits
    const loadVisits = useCallback(async () => {
        setIsLoading(true);
        // Force date range to ensure old data shows up (bypass default state issues)
        const today = new Date().toISOString().split('T')[0];
        let url = `/api/visits?from=2020-01-01&to=${today}&view=map&_t=${Date.now()}`; // Add timestamp to break cache
        if (selectedTeam) url += `&team=${selectedTeam}`;

        console.log('Fetching visits map:', url);
        try {
            const { data } = await api<Visit[]>(url);
            console.log('Visits map result:', data);

            if (data && Array.isArray(data)) {
                // Support both latlng string and lat/lng numbers
                const validVisits = data.filter(v => v.latlng || (typeof v.lat === 'number' && typeof v.lng === 'number'));
                console.log('Valid visits with latlng:', validVisits.length);
                setVisits(validVisits);
            }
        } catch (error) {
            console.error('Error loading visits map:', error);
        }
        setIsLoading(false);
    }, [dateRange, selectedTeam]);

    useEffect(() => {
        loadVisits();
    }, [loadVisits]);

    // Initialize map
    useEffect(() => {
        if (!mapRef.current || !window.google?.maps) return;
        if (mapInstanceRef.current) return;

        mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 15.87, lng: 100.99 },
            zoom: 6,
            mapTypeControl: true,
            mapTypeControlOptions: {
                style: window.google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                position: window.google.maps.ControlPosition.TOP_RIGHT,
            },
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true,
            zoomControlOptions: {
                position: window.google.maps.ControlPosition.RIGHT_CENTER,
            },
            styles: [
                { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
            ],
        });

        infoWindowRef.current = new window.google.maps.InfoWindow();
        setMapReady(true);
    }, []);

    // Toggle Location Tracking
    const handleToggleTracking = () => {
        if (!navigator.geolocation) {
            alert('Browser does not support geolocation');
            return;
        }

        if (isTracking) {
            // Stop tracking
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
                watchIdRef.current = null;
            }
            if (userMarkerRef.current) {
                userMarkerRef.current.setMap(null);
                userMarkerRef.current = null;
            }
            setIsTracking(false);
            setUserLocation(null);
        } else {
            // Start tracking
            setIsTracking(true);
            watchIdRef.current = navigator.geolocation.watchPosition(
                (position) => {
                    const pos = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setUserLocation(pos);

                    if (mapInstanceRef.current && window.google?.maps) {
                        if (!userMarkerRef.current) {
                            userMarkerRef.current = new window.google.maps.Marker({
                                position: pos,
                                map: mapInstanceRef.current,
                                title: 'ตำแหน่งปัจจุบันของคุณ',
                                icon: {
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    scale: 8,
                                    fillColor: '#3B82F6', // Blue color
                                    fillOpacity: 1,
                                    strokeColor: 'white',
                                    strokeWeight: 2,
                                },
                                zIndex: 999, // On top of other markers
                            });
                        } else {
                            userMarkerRef.current.setPosition(pos);
                        }
                    }
                },
                (error) => {
                    console.error('Error tracking location:', error);
                    setIsTracking(false);
                    alert('ไม่สามารถระบุตำแหน่งได้: ' + error.message);
                },
                { enableHighAccuracy: true }
            );
        }
    };

    // Center map on user location if available/tracking
    useEffect(() => {
        if (userLocation && mapInstanceRef.current && isTracking) {
            mapInstanceRef.current.panTo(userLocation);
            if (mapInstanceRef.current.getZoom()! < 12) {
                mapInstanceRef.current.setZoom(12);
            }
        }
    }, [userLocation, isTracking]);

    // Cleanup watchId
    useEffect(() => {
        return () => {
            if (watchIdRef.current !== null) {
                navigator.geolocation.clearWatch(watchIdRef.current);
            }
        };
    }, []);


    // Filter visits by hidden teams
    const visibleVisits = visits.filter(v => !hiddenTeams.has(v.team));

    // Update markers and clusters
    useEffect(() => {
        if (!mapInstanceRef.current || !mapReady) return;

        // Clear existing clusterer
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
        }

        // Clear existing markers
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        if (visibleVisits.length === 0) return;

        const bounds = new window.google.maps.LatLngBounds();

        visibleVisits.forEach(visit => {
            let position: { lat: number; lng: number } | null = null;

            if (typeof visit.lat === 'number' && typeof visit.lng === 'number') {
                position = { lat: visit.lat, lng: visit.lng };
            } else if (visit.latlng) {
                const [lat, lng] = visit.latlng.split(',').map(Number);
                if (!isNaN(lat) && !isNaN(lng)) {
                    position = { lat, lng };
                }
            }

            if (!position) return;
            bounds.extend(position);

            const teamData = teams.find(t => t.code === visit.team);
            // Use explicit color map or fallback
            const teamColor = TEAM_COLORS[visit.team] || teamData?.color || '#F97316';

            const marker = new window.google.maps.Marker({
                position,
                // map: mapInstanceRef.current, // Do NOT set map here, clusterer will handle it
                title: visit.customer_name,
                icon: {
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 12,
                    fillColor: teamColor,
                    fillOpacity: 0.9,
                    strokeColor: '#fff',
                    strokeWeight: 2,
                },
            });

            marker.addListener('click', () => {
                if (!infoWindowRef.current) return;
                infoWindowRef.current.setContent(`
                    <div style="padding: 12px; min-width: 200px; font-family: 'Inter', sans-serif;">
                        <h3 style="margin: 0 0 8px 0; font-weight: 600; font-size: 16px; color: #1a1a1a;">${visit.customer_name}</h3>
                        <div style="margin: 8px 0; padding: 8px; background: #f5f5f5; border-radius: 8px;">
                            <p style="margin: 4px 0; color: #666; font-size: 13px;">📍 ${visit.district || '-'}, ${visit.province || '-'}</p>
                            <p style="margin: 4px 0; color: #666; font-size: 13px;">📅 ${visit.date}</p>
                            <p style="margin: 4px 0; color: #666; font-size: 13px;">
                                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${teamColor}; margin-right: 6px;"></span>
                                ${teamData?.name || `ทีม ${visit.team}`}
                            </p>
                            ${visit.phone ? `<p style="margin: 4px 0; color: #666; font-size: 13px;">📞 ${visit.phone}</p>` : ''}
                        </div>
                        <a href="https://www.google.com/maps?q=${visit.latlng}" target="_blank" 
                           style="display: inline-block; margin-top: 8px; padding: 8px 16px; background: #F97316; color: white; text-decoration: none; border-radius: 8px; font-size: 13px; font-weight: 500;">
                            🗺️ เปิดใน Google Maps
                        </a>
                    </div>
                `);
                infoWindowRef.current.open(mapInstanceRef.current, marker);
            });

            markersRef.current.push(marker);
        });

        // Initialize Clusterer
        if (!clustererRef.current) {
            clustererRef.current = new MarkerClusterer({
                map: mapInstanceRef.current,
                markers: markersRef.current,
            });
        } else {
            clustererRef.current.addMarkers(markersRef.current);
        }

        // Fit bounds for viewing all markers (only if not tracking user location)
        if (markersRef.current.length > 0 && !isTracking) {
            // Optional: Don't autofit every time filters change if it's annoying
            // mapInstanceRef.current.fitBounds(bounds); 
        }

    }, [visibleVisits, teams, mapReady, isTracking]);

    // Map controls
    const handleResetView = () => {
        mapInstanceRef.current?.setCenter({ lat: 15.87, lng: 100.99 });
        mapInstanceRef.current?.setZoom(6);
    };

    const handleFitMarkers = () => {
        if (markersRef.current.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        markersRef.current.forEach(m => {
            const pos = m.getPosition();
            if (pos) bounds.extend(pos);
        });
        mapInstanceRef.current?.fitBounds(bounds);
    };

    const toggleTeam = (teamCode: string) => {
        setHiddenTeams(prev => {
            const next = new Set(prev);
            if (next.has(teamCode)) {
                next.delete(teamCode);
            } else {
                next.add(teamCode);
            }
            return next;
        });
    };

    // Stats
    const teamStats = teams.map(t => ({
        ...t,
        count: visits.filter(v => v.team === t.code).length,
        visible: !hiddenTeams.has(t.code),
        color: TEAM_COLORS[t.code] || t.color || '#F97316', // Use explicit color
    })).filter(t => t.count > 0);

    return (
        <div className="space-y-4">
            <PageHeader title="แผนที่ลูกค้า" subtitle="แสดงที่ตั้งลูกค้าบนแผนที่" />

            {/* Filters Card */}
            <Card padding="md">
                {/* Date Presets */}
                <div className="flex flex-wrap gap-2 mb-4">
                    {[
                        { key: 'all', label: 'ทั้งหมด' },
                        { key: 'today', label: 'วันนี้' },
                        { key: '7days', label: '7 วัน' },
                        { key: '30days', label: '30 วัน' },
                        { key: 'month', label: 'เดือนนี้' },
                        { key: 'custom', label: 'กำหนดเอง' },
                    ].map(p => (
                        <button
                            key={p.key}
                            onClick={() => applyDatePreset(p.key as DatePreset)}
                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${datePreset === p.key
                                ? 'bg-primary text-white shadow-md'
                                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Custom Date Range */}
                {datePreset === 'custom' && (
                    <div className="flex flex-wrap gap-4 mb-4 p-4 bg-neutral-50 rounded-xl">
                        <div className="flex-1 min-w-[140px]">
                            <label className="label">จากวันที่</label>
                            <input
                                type="date"
                                className="input"
                                value={dateRange.from}
                                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                            />
                        </div>
                        <div className="flex-1 min-w-[140px]">
                            <label className="label">ถึงวันที่</label>
                            <input
                                type="date"
                                className="input"
                                value={dateRange.to}
                                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                            />
                        </div>
                    </div>
                )}

                {/* Team Filter & Search Button */}
                <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[150px]">
                        <Select
                            label="กรองทีม"
                            value={selectedTeam}
                            onChange={(e) => setSelectedTeam(e.target.value)}
                            options={[
                                { value: '', label: '🏢 ทุกทีม' },
                                ...teams.map(t => ({ value: t.code, label: `${t.name}` })),
                            ]}
                        />
                    </div>
                    <Button onClick={loadVisits} variant="primary" loading={isLoading} className="h-[42px]">
                        🔍 ค้นหา
                    </Button>
                </div>
            </Card>

            {/* Team Legend (clickable to toggle visibility) */}
            {teamStats.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    <div className="bg-white rounded-xl px-4 py-2 shadow-soft font-medium">
                        📍 รวม <strong className="text-primary">{visibleVisits.length}</strong> จุด
                    </div>
                    {teamStats.map(t => (
                        <button
                            key={t.code}
                            onClick={() => toggleTeam(t.code)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl shadow-soft transition-all ${t.visible
                                ? 'bg-white hover:bg-neutral-50'
                                : 'bg-neutral-200 opacity-50'
                                }`}
                        >
                            <span
                                className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                                style={{ backgroundColor: t.visible ? t.color : '#ccc' }}
                            />
                            <span className={t.visible ? '' : 'line-through'}>{t.name}</span>
                            <strong>{t.count}</strong>
                        </button>
                    ))}
                </div>
            )}

            {/* Map Container */}
            <Card padding="none" className="overflow-hidden relative">
                {/* Loading Overlay */}
                {isLoading && (
                    <div className="absolute inset-0 bg-white/80 z-20 flex items-center justify-center">
                        <div className="text-center">
                            <Spinner />
                            <p className="mt-2 text-neutral-500">กำลังโหลดข้อมูล...</p>
                        </div>
                    </div>
                )}

                {/* Map Controls */}
                <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                    <button
                        onClick={handleResetView}
                        className="bg-white p-3 rounded-xl shadow-lg hover:bg-neutral-50 transition-all"
                        title="รีเซ็ตมุมมอง"
                    >
                        🌏
                    </button>
                    <button
                        onClick={handleFitMarkers}
                        className="bg-white p-3 rounded-xl shadow-lg hover:bg-neutral-50 transition-all"
                        title="แสดงทุกจุด"
                    >
                        🎯
                    </button>
                    <button
                        onClick={handleToggleTracking}
                        className={`p-3 rounded-xl shadow-lg transition-all ${isTracking ? 'bg-blue-500 text-white' : 'bg-white hover:bg-neutral-50'}`}
                        title="ตำแหน่งปัจจุบัน"
                    >
                        📍
                    </button>
                </div>

                {/* Map */}
                <div
                    ref={mapRef}
                    className="w-full h-[450px] sm:h-[500px] md:h-[600px]"
                />

                {/* Empty State */}
                {!isLoading && visibleVisits.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-10">
                        <EmptyState
                            icon="🗺️"
                            title="ไม่มีข้อมูลในช่วงเวลานี้"
                            description="ลองเปลี่ยนช่วงวันที่หรือทีมเพื่อดูข้อมูล"
                        />
                    </div>
                )}

                {/* Google Maps Loading */}
                {!window.google?.maps && (
                    <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 z-10">
                        <div className="text-center">
                            <div className="text-4xl mb-4">🗺️</div>
                            <p className="text-neutral-500">กำลังโหลด Google Maps...</p>
                        </div>
                    </div>
                )}
            </Card>

            {/* Tips */}
            <div className="text-center text-sm text-neutral-500">
                💡 คลิกที่หมุดเพื่อดูรายละเอียด | คลิกที่ชื่อทีมเพื่อซ่อน/แสดง
            </div>
        </div>
    );
}
